/* ==============================================================================
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
============================================================================== */

import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

// @mui material components
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDBadgeDot from 'components/MDBadgeDot';

// Material Dashboard 2 PRO React examples
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Footer from 'examples/Footer';
import DefaultLineChart from 'examples/Charts/LineCharts/DefaultLineChart';

import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';

// Material Dashboard 2 PRO React examples
import Navbar from 'examples/Navbars/NavBar';
import { Backdrop, CircularProgress, InputAdornment, MenuItem } from '@mui/material';
import MDInput from 'components/MDInput';

const initFind = {
  branch: 0,
  year: '',
  month: '',
};

const initChart = {
  title: '',
  description: '',
  date: '',
  chart: {
    labels: [],
    datasets: [],
  },
};

function DashboardGP() {
  const mod = 'dashboard_gp';
  const { t, i18n } = useTranslation();
  const app = useSelector((state) => state[APP_STATE]);
  const [cboBranch, setCboBranch] = useState([]);
  const [cboYear, setCboYear] = useState([]);
  const [cboMonth, setCboMonth] = useState([]);
  const [find, setFind] = useState(initFind);
  const [loading, setLoading] = useState(false);
  const [chartLine, setChartLine] = useState(initChart);

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setCboBranch(result.branch);
        setCboYear(result.year);
        setCboMonth(result.month);
      }
    });
  };

  const view = (data) => {
    setFind(data);
    const period = data.year && data.month ? `${data.year}-${data.month}` : null;

    if (data.branch && period) {
      setLoading(true);
      FetchData('POST', `/${mod}`, app.token, {
        branch: data.branch,
        period: period,
      }).then((result) => {
        if (result.status === 'ok') {
          setChartLine(result.line);
        }
        setLoading(false);
      });
    }
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={5} lg={5} xl={3}>
            <MDInput
              fullWidth
              value={find.branch}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, branch: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('menu.branch')} :</InputAdornment>
                ),
              }}
            >
              {cboBranch.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={3} xl={2}>
            <MDInput
              fullWidth
              select
              value={find.year}
              onChange={(e) => view({ ...find, year: e.target.value })}
              InputProps={{
                startAdornment: <InputAdornment position='start'>{t('ปี')} :</InputAdornment>,
              }}
            >
              {cboYear.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={3} xl={2}>
            <MDInput
              fullWidth
              select
              value={find.month}
              onChange={(e) => view({ ...find, month: e.target.value })}
              InputProps={{
                startAdornment: <InputAdornment position='start'>{t('เดือน')} :</InputAdornment>,
              }}
            >
              {cboMonth.map((m) => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
        </Grid>
      </Card>
    );
  };

  const renderChart = () => {
    return (
      <MDBox mb={3} mt={1}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <DefaultLineChart
              // title='ยอดขายหน้าร้านแยกตามสาขา'
              description={
                <MDBox display='flex' justifyContent='space-between'>
                  <MDBox display='flex' ml={-1}>
                    {chartLine.chart.datasets.map((row) => (
                      <MDBadgeDot
                        color={row.color}
                        size='sm'
                        badgeContent={row.label}
                        key={row.label}
                      />
                    ))}
                  </MDBox>
                </MDBox>
              }
              chart={chartLine.chart}
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, []);

  return (
    <DashboardLayout>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
      <Navbar route={[t(`menu.dashboard`), t(`menu.dashboard_gp`)]} />
      <MDBox py={3}>
        {renderSearch()}
        {chartLine && chartLine.chart.datasets.length > 0 ? (
          renderChart()
        ) : (
          <MDBox mt={3} textAlign='center'>
            กรุณากรอกข้อมูลให้ครบถ้วน
          </MDBox>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default DashboardGP;
